/* eslint-disable no-undef */
import SecondarySidebar from 'components/SecondarySidebar/SecondarySidebar'
import RoomIcon from '@mui/icons-material/Room'
import { Dispatch, SetStateAction } from 'react'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import IconDropdown from 'components/IconDropdown/IconDropdown'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { DeliveryAreaGroup } from 'types/model/common'
import { CollectionPoint } from 'types/model/serviceAreas'
import { Box, Typography } from '@mui/material'
import CircleIcon from '@mui/icons-material/Circle'
import theme from 'lib/theme'
import { useQueryClient } from 'react-query'
import { useServiceAreas } from 'providers/ServiceAreasProvider'
import { SidebarBtn } from './ServiceArea.styled'
import { getColorFromIndex } from './utils'

type ServiceAreaSidebarProps = {
  handleOpenModal: (selectedValue: string, innerValue: string) => void
  placeId: string
  setDrawnPath: Dispatch<SetStateAction<{ lat: number; lng: number }[][] | []>>
  overlays: google.maps.Polygon[] | []
  deleteShapes: (overlays: google.maps.Polygon[] | []) => void
  setIsEditing: Dispatch<SetStateAction<boolean>>
  setDeliveryAreaId: Dispatch<SetStateAction<number | null>>
}

const NEW_DELIVERY_GROUP = 'NewDeliveryGroup'
const NEW_COLLECTION_POINT = 'NewCollectionPoint'

const createOptions = () => {
  const { t } = useTranslation()
  return [
    {
      label: t('serviceArea.newDeliveryAreaGroup'),
      value: NEW_DELIVERY_GROUP,
    },
    {
      label: t('serviceArea.newCollectionPoint'),
      value: NEW_COLLECTION_POINT,
    },
  ]
}

export const ServiceAreaSidebar = ({
  handleOpenModal,
  placeId,
  setDrawnPath,
  overlays,
  deleteShapes,
  setIsEditing,
  setDeliveryAreaId,
}: ServiceAreaSidebarProps) => {
  const {
    allDeliveryAreas,
    storeName,
    setCoordinates,
    coordinateGroups,
    setSelectedDeliveryArea,
    selectedDeliveryArea,
    collectionPoints,
    setSelectedCollectionPoint,
    selectedCollectionPoint,
  } = useServiceAreas()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const handleResetEditing = () => {
    setDrawnPath([])
    setIsEditing(false)
    setDeliveryAreaId(null)
    deleteShapes(overlays)
  }

  const handleClickCollectionPoint = (collectionPoint: CollectionPoint) => {
    setCoordinates([])
    setSelectedDeliveryArea({ id: NaN, name: '' })
    setSelectedCollectionPoint({
      id: collectionPoint.id,
      name: collectionPoint.name,
      latitude: collectionPoint.latitude,
      longitude: collectionPoint.longitude,
    })
    handleResetEditing()
  }

  const handleClickDeliveryArea = (group: DeliveryAreaGroup) => {
    setCoordinates(
      coordinateGroups.filter(
        (coordinateGroup) => coordinateGroup[0].deliveryAreaGroupId === group.id
      )
    )
    setSelectedDeliveryArea({ id: group.id, name: group.name })
    setSelectedCollectionPoint({
      id: NaN,
      name: '',
      latitude: NaN,
      longitude: NaN,
    })
    handleResetEditing()
  }

  const handleClickAll = () => {
    setCoordinates(coordinateGroups || null)
    setSelectedDeliveryArea({ id: NaN, name: '' })
    setSelectedCollectionPoint({
      id: NaN,
      name: '',
      latitude: NaN,
      longitude: NaN,
    })
    handleResetEditing()
  }
  return (
    <SecondarySidebar
      selectOptionsBtn={
        <IconDropdown
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          minWidth='200px'
          Icon={MoreHorizIcon}
          isIconOnly
          options={createOptions()}
          ariaLabel={t('serviceArea.createNewServiceAreaOptions-Aria')}
          onChange={handleOpenModal}
        />
      }
      scrollable={Boolean(
        !selectedDeliveryArea.id && !selectedCollectionPoint.id
      )}
      header={t('serviceAreas')}
      subtitle={storeName}
      onGoBackClick={() => {
        navigate(`/place/${placeId}/info`)
        queryClient.cancelQueries('getCoordinates')
      }}>
      <Typography
        variant='body2'
        mb={2}
        mt={2}
        sx={{ color: theme.palette.text.secondary }}>
        {t('serviceArea.deliveryAreaGroups').toUpperCase()}
      </Typography>
      <SidebarBtn
        onClick={handleClickAll}
        selected={
          !Boolean(selectedDeliveryArea.id) && !selectedCollectionPoint.id
        }>
        <Typography variant='body2'>{t('all')}</Typography>
      </SidebarBtn>
      {allDeliveryAreas &&
        allDeliveryAreas.map((group: DeliveryAreaGroup, index) => (
          <SidebarBtn
            data-cy={`Delivery area group ${group.name}`}
            selected={selectedDeliveryArea.id === group.id}
            key={group.id}
            onClick={() => handleClickDeliveryArea(group)}>
            <Box display='flex' alignItems='center'>
              <CircleIcon
                fontSize='small'
                sx={{
                  fill: getColorFromIndex(index),
                  '&:hover': {
                    fill: getColorFromIndex(index),
                  },
                }}
              />
              <Box>
                <Typography variant='body2' ml={1} sx={{ textAlign: 'start' }}>
                  {group.name}
                </Typography>
                <Typography variant='body2' ml={1} sx={{ textAlign: 'start' }}>
                  {group.id}
                </Typography>
              </Box>
            </Box>
          </SidebarBtn>
        ))}
      <Typography
        variant='body2'
        mt={3}
        sx={{ color: theme.palette.text.secondary }}>
        {t('serviceArea.collectionPoints').toUpperCase()}
      </Typography>
      {collectionPoints &&
        collectionPoints.map(
          (collectionPoint: CollectionPoint, index: number) => (
            <SidebarBtn
              selected={selectedCollectionPoint.id === collectionPoint.id}
              key={collectionPoint.id}
              onClick={() => handleClickCollectionPoint(collectionPoint)}>
              <Box display='flex' alignItems='center'>
                <RoomIcon
                  fontSize='small'
                  sx={{
                    fill: getColorFromIndex(index),
                    '&:hover': {
                      fill: getColorFromIndex(index),
                    },
                  }}
                />
                <Typography variant='body2' ml={1} sx={{ textAlign: 'start' }}>
                  {collectionPoint.name}
                </Typography>
              </Box>
            </SidebarBtn>
          )
        )}
    </SecondarySidebar>
  )
}
